import React, { useState, useEffect, useRef } from 'react';
import { ImagePlus, Loader2, Film } from 'lucide-react';
import { collateralApi } from '@/api/collateral';

/**
 * Renders a template's server-rendered gallery thumbnail (its resting design).
 * Fetches the PNG as a Blob via the authed API client (so bearer/cookie auth
 * applies) and shows it as an object URL, cleaning up on unmount.
 *
 * `animated` overlays a small film badge so video templates read at a glance.
 */
export default function CollateralThumb({ templateId, animated = false, className = '' }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    (async () => {
      try {
        const blob = await collateralApi.thumbnail(templateId);
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setUrl(u);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [templateId]);

  return (
    <div className={`relative bg-muted/40 flex items-center justify-center overflow-hidden ${className}`}>
      {url ? (
        <img src={url} alt="" data-testid={`thumb-${templateId}`} className="max-h-full max-w-full object-contain" />
      ) : failed ? (
        <ImagePlus className="h-6 w-6 text-muted-foreground/40" />
      ) : (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/40" />
      )}
      {animated && (
        <span className="absolute bottom-1 right-1 flex items-center gap-0.5 rounded bg-black/60 px-1 py-0.5 text-[9px] font-medium text-white">
          <Film className="h-2.5 w-2.5" /> MP4
        </span>
      )}
    </div>
  );
}
